import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { stripeEndpoints } from '../../services/api/endpoints/stripe.endpoint';

interface StripeState {
  recurrencePlans: any;
  oneOfPlans: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: StripeState = {
  recurrencePlans: [],
  oneOfPlans: [],
  isLoading: false,
  error: null,
};

// Filter the plans based on the type
const filterPlansByType = (plans: any[], type: string) => {
  // return plans.filter(plan => plan.type === type);
  return plans.filter(plan => plan.product.metadata.cacxi_type !== 'tokens' && plan.type === type);
};

const getPlans = (plans: any[]) => {
  const recurrencePlans = filterPlansByType(plans, 'recurring');
  const oneOfPlans = filterPlansByType(plans, 'one_time');

  // filter if the product is active
  const activeRecurrencePlans = recurrencePlans.filter(plan => plan.product.active);
  const activeOneOfPlans = oneOfPlans.filter(plan => plan.product.active);
  return {
    recurrencePlans: activeRecurrencePlans,
    oneOfPlans: activeOneOfPlans,
  };
};

// Async thunk for fetching stripe data
export const fetchStripeData = createAsyncThunk(
  'stripe/fetchStripeData',
  async (_, { rejectWithValue }) => {
    try {
      return await stripeEndpoints.getPlans();
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch stripe data');
    }
  }
);

// Create the slice
const stripeStateSlice = createSlice({
  name: 'stripe',
  initialState,
  reducers: {
    clearStripeData(state) {
      state.recurrencePlans = [];
      state.oneOfPlans = [];
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchStripeData.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStripeData.fulfilled, (state, action) => {
        state.isLoading = false;
        const { recurrencePlans, oneOfPlans } = getPlans(action.payload);
        state.recurrencePlans = recurrencePlans;
        state.oneOfPlans = oneOfPlans;
        state.error = null;
      })
      .addCase(fetchStripeData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearStripeData } = stripeStateSlice.actions;

export const selectStripe = (state: RootState) => state.stripe;

export default stripeStateSlice.reducer;
